import { ChangeEvent, Fragment, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import {
  Button,
  CancelSubmitButtons,
  CardInputsContainer,
  CodeVerification,
  VerifyPhoneNumberModal,
} from "~/common/components";
import { getDisabledClasses } from "~/common/components/ui/FieldsetCard";
import Input from "~/common/components/ui/Input";
import {
  CODE_VERIFICATION_CHANNELS,
  CODE_VERIFICATION_SCOPES,
  COUNTRY_CODES,
  DEFAULT_CODE_LENGTH,
  INITIAL_VERIFICATION_SESSION,
} from "~/common/constants/verificationCode";
import { useCodeVerificationCooldown } from "~/common/hooks/useCodeVerificationCooldown";
import { customErrorSchema } from "~/common/types";
import { CodeVerificationSession } from "~/common/types/verificationCode";
import {
  useCancelVerificationCode,
  useFormatHook,
  useRetryVerificationCode,
  useSendVerificationCode,
  useVerifyCode,
} from "~/patients/hooks";
import { useUpdatePhoneNumber } from "~/patients/hooks/useUpdatePhoneNumber";
import { PatientAccountAccessForm } from "~/patients/types/settings";
import {
  PatientInformation,
  updatePatientAccountAccessSchema,
} from "~/patients/utils";

interface AccountAccessProps {
  currentPatient: PatientInformation;
}

export const AccountAccess = ({ currentPatient }: AccountAccessProps) => {
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [invalidCodeErrorMessage, setInvalidCodeErrorMessage] =
    useState<string>();
  const [isEditing, setIsEditing] = useState(false);
  const [currentCode, setCurrentCode] = useState("");
  const { formatPhoneNumber, stripPhoneNumber } = useFormatHook();
  const queryClient = useQueryClient();
  const [verificationSession, setVerificationSession] =
    useState<CodeVerificationSession>(INITIAL_VERIFICATION_SESSION);

  const currentPatientPhoneNumber = currentPatient.phone_number?.number
    ? formatPhoneNumber(currentPatient.phone_number.number)
    : "";

  const { cooldown } = useCodeVerificationCooldown(
    verificationSession.canResendAt,
  );

  const {
    reset,
    register,
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = useForm<PatientAccountAccessForm>({
    resolver: zodResolver(updatePatientAccountAccessSchema),
    defaultValues: {
      email: currentPatient.email ?? "",
      phone_number: currentPatientPhoneNumber,
    },
    mode: "all",
  });

  const selectedPatientPhoneNumber = watch("phone_number");

  const { mutate: sendVerificationCode, isPending: isSendingCode } =
    useSendVerificationCode();
  const { mutate: verifyCode, isPending: isVerifyingCode } = useVerifyCode();
  const { mutate: cancelVerificationCode, isPending: isCancellingCode } =
    useCancelVerificationCode();
  const { mutate: retryVerificationCode, isPending: isRetryingCode } =
    useRetryVerificationCode();

  const { mutate: updatePhoneNumber } = useUpdatePhoneNumber();

  const onEdit = () => setIsEditing(true);

  const onCancel = () => {
    reset();
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const isValid = await trigger();

      if (!isValid) return;

      if (cooldown.active) {
        return toast.warning(
          `Cannot send SMS right now. Please wait ${cooldown.remainingTime} seconds before trying again.`,
        );
      }

      const phoneNumberChanged =
        selectedPatientPhoneNumber !== currentPatientPhoneNumber;

      if (!phoneNumberChanged) {
        return toast.error("You haven't changed your mobile number.");
      }

      setShowVerifyModal(true);

      sendVerificationCode(
        {
          channel: CODE_VERIFICATION_CHANNELS.SMS,
          scope: CODE_VERIFICATION_SCOPES.PHONE_UPDATE,
          phone_number: {
            number: stripPhoneNumber(selectedPatientPhoneNumber),
            country_code: COUNTRY_CODES.US,
          },
        },
        {
          onSuccess: ({ can_resend_at, verification_token }) => {
            setVerificationSession((prev) => ({
              ...prev,
              canResendAt: new Date(can_resend_at).getTime(),
              token: verification_token,
              isExpired: false,
            }));
          },
        },
      );
    } catch (_error) {
      return;
    }
  };

  const handleVerify = () => {
    if (!verificationSession.token) return;

    if (currentCode.length < DEFAULT_CODE_LENGTH) {
      setInvalidCodeErrorMessage(
        `${DEFAULT_CODE_LENGTH} character code is required`,
      );
      return;
    }

    verifyCode(
      {
        verification_code: currentCode,
        verification_token: verificationSession.token,
      },
      {
        onSuccess: ({ one_use_token }) => {
          setVerificationSession(INITIAL_VERIFICATION_SESSION);
          setCurrentCode("");
          updatePhoneNumber(
            {
              verification_token: one_use_token,
              phone_number: {
                number: stripPhoneNumber(selectedPatientPhoneNumber),
                country_code: COUNTRY_CODES.US,
              },
              user_id: currentPatient.id,
            },
            {
              onSuccess: () => {
                queryClient
                  .invalidateQueries({ queryKey: ["getCurrentPatient"] })
                  .catch(() => {
                    toast.error("Failed to refresh patient data.");
                  });
                setShowVerifyModal(false);
                setIsEditing(false);
                toast.success("Mobile number updated");
              },
            },
          );
        },
        onError: (error) => {
          const result = customErrorSchema.safeParse(error);
          if (result.success) {
            if (result.data.response.data.detail === "Token expired") {
              setInvalidCodeErrorMessage(
                "Code has expired, please try another one.",
              );
              setVerificationSession((prev) => ({
                ...prev,
                isExpired: true,
              }));
              return;
            }
          }
          setInvalidCodeErrorMessage(
            "This code doesn't match. Please try again.",
          );
        },
      },
    );
  };

  const handleClose = () => {
    setShowVerifyModal(false);
    setInvalidCodeErrorMessage(undefined);
    setCurrentCode("");

    if (!verificationSession.token) return;

    cancelVerificationCode(
      { verification_token: verificationSession.token },
      {
        onSuccess: ({ can_resend_at, verification_token }) => {
          setVerificationSession((prev) => ({
            ...prev,
            canResendAt: new Date(can_resend_at).getTime(),
            token: verification_token,
          }));
        },
      },
    );
  };

  const restartVerificationFlow = () => {
    if (!verificationSession.token) return;
    cancelVerificationCode(
      {
        verification_token: verificationSession.token,
      },
      {
        onSuccess: () => {
          sendVerificationCode(
            {
              phone_number: {
                number: stripPhoneNumber(selectedPatientPhoneNumber),
                country_code: COUNTRY_CODES.US,
              },
              scope: CODE_VERIFICATION_SCOPES.PHONE_UPDATE,
              channel: CODE_VERIFICATION_CHANNELS.SMS,
            },
            {
              onSuccess: ({ verification_token, can_resend_at }) => {
                setVerificationSession((prev) => ({
                  ...prev,
                  token: verification_token,
                  canResendAt: new Date(can_resend_at).getTime(),
                  isExpired: false,
                }));
              },
            },
          );
        },
      },
    );
  };

  const handleRetry = () => {
    if (!verificationSession.token) return;

    if (verificationSession.isExpired) {
      restartVerificationFlow();
      return;
    }

    retryVerificationCode(
      {
        verification_token: verificationSession.token,
      },
      {
        onSuccess: ({ can_resend_at, verification_token }) => {
          setVerificationSession((prev) => ({
            ...prev,
            canResendAt: new Date(can_resend_at).getTime(),
            token: verification_token,
          }));
        },
        onError: (error) => {
          const result = customErrorSchema.safeParse(error);
          if (result.success) {
            if (result.data.response.data.detail === "Token expired") {
              restartVerificationFlow();
              return;
            }
            toast.error(result.data.response.data.detail);
          }
        },
      },
    );
  };

  const handleChange = (code: string) => {
    setCurrentCode(code);
    setInvalidCodeErrorMessage(undefined);
  };

  const shouldDisableCodeInput =
    isSendingCode || isVerifyingCode || isRetryingCode || isCancellingCode;

  return (
    <Fragment>
      <VerifyPhoneNumberModal
        show={showVerifyModal}
        onClose={handleClose}
        onVerify={handleVerify}
        className="max-w-xl"
      >
        <CodeVerification
          value={currentCode}
          onChange={handleChange}
          onRetry={handleRetry}
          inCooldownTime={cooldown.active}
          remainingTime={cooldown.remainingTime}
          disabled={shouldDisableCodeInput}
          error={invalidCodeErrorMessage}
          instructions={`Enter the code sent by SMS to ${selectedPatientPhoneNumber}`}
        />
      </VerifyPhoneNumberModal>
      <CardInputsContainer
        title="Account access"
        containerClassName="flex-row items-center"
        actions={
          isEditing ? (
            <CancelSubmitButtons
              containerClassName="w-auto"
              submitButtonLabel="Save"
              handleCancel={onCancel}
              handleSubmit={handleSave}
              submitButtonType="button"
            />
          ) : (
            <Button onClick={onEdit} className="bg-white" variant="outline">
              Edit
            </Button>
          )
        }
      >
        <form className="grid gap-4 md:grid-cols-2">
          <Input
            id="email"
            label="Login email"
            disabled
            {...getDisabledClasses(!isEditing)}
            {...register("email")}
            error={errors?.email?.message}
          />
          <Input
            id="mobile"
            label="Mobile number"
            disabled={!isEditing}
            {...getDisabledClasses(!isEditing)}
            {...register("phone_number", {
              onChange: (event: ChangeEvent<HTMLInputElement>) => {
                setValue("phone_number", formatPhoneNumber(event.target.value));
              },
            })}
            error={errors?.phone_number?.message}
          />
        </form>
      </CardInputsContainer>
    </Fragment>
  );
};
